import { API } from '@repo/hooks';
import { queryClient } from '@repo/lib/react-query';
import {
    ORDERS_API_READY_EVENT,
    ORDERS_API_WINDOW_KEY,
    type OrdersAssetType,
    type OrdersExternalApi,
    type OrdersExternalState,
} from '@sp-saljapro/orders-external-api';

import { TELEMETRY_LOGS, logTelemetry } from '@/lib/telemetry.lib';

import type { OrderTakingWindow } from '@/api/types/window.types';

declare const window: OrderTakingWindow;

let state: OrdersExternalState = { assetData: null };
const listeners = new Set<() => void>();

const getSnapshot = (): OrdersExternalState => state;

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const notify = () => {
    for (const listener of listeners) {
        listener();
    }
};

export const setOrdersExternalState = (next: OrdersExternalState): void => {
    if (state === next) {
        return;
    }
    state = next;
    notify();
};

const invalidateAsset = async (params: { assetId: string; assetType: OrdersAssetType }): Promise<void> => {
    logTelemetry('info', TELEMETRY_LOGS.InvalidateAsset, {
        invalidateAsset: {
            assetId: params.assetId,
            assetType: params.assetType,
        },
    });
    await queryClient.invalidateQueries({
        queryKey: [API.assetDetails, params.assetType, params.assetId],
    });
};

/**
 * Initialize the Orders external API on `window.salja.orders.api`
 * and dispatch the ready event for waiting consumers.
 */
export const initOrdersExternalApi = (): void => {
    const api: OrdersExternalApi = {
        store: {
            getSnapshot,
            subscribe,
        },
        commands: {
            invalidateAsset,
        },
    };

    window.salja.orders = {
        ...window.salja.orders,
        [ORDERS_API_WINDOW_KEY]: api,
    };

    globalThis.dispatchEvent(new CustomEvent(ORDERS_API_READY_EVENT));
};
